import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  User, Heart, Calendar as CalIcon, LogOut, Download, MapPin, Loader2, Edit3, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageLayout from "@/components/layout/PageLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Price } from "@/context/CurrencyContext";
import Seo from "@/components/Seo";

const STATUS_BADGES = {
  draft: "bg-cream-200 text-foreground",
  confirmed: "bg-emerald-600 text-white",
  cancelled: "bg-foreground/40 text-white",
  completed: "bg-saffron text-white",
};
const PAY_BADGES = {
  pending: "bg-amber-500 text-white",
  paid: "bg-emerald-600 text-white",
  failed: "bg-red-600 text-white",
  expired: "bg-foreground/40 text-white",
};

export default function Profile() {
  const { user, loading: authLoading, logout, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", country: "" });

  useEffect(() => {
    if (!user) return;
    setForm({ full_name: user.full_name || user.name || "", phone: user.phone || "", country: user.country || "" });
    (async () => {
      try {
        const [b, w] = await Promise.all([
          api.get("/bookings/mine"),
          api.get("/wishlist"),
        ]);
        setBookings(b.data);
        setWishlist(w.data);
      } catch (_) {}
      setLoading(false);
    })();
  }, [user]);

  const saveProfile = async () => {
    try {
      await api.patch("/auth/profile", form);
      await refresh();
      toast.success("Profile saved");
      setEditing(false);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const removeFromWishlist = async (slug) => {
    try {
      await api.delete(`/wishlist/${slug}`);
      setWishlist((prev) => prev.filter((t) => t.slug !== slug));
      toast.success("Removed");
    } catch (_) {
      toast.error("Failed");
    }
  };

  if (authLoading) return <PageLayout><div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-saffron" /></div></PageLayout>;
  if (!user) return <Navigate to="/" replace />;

  return (
    <PageLayout>
      <Seo title="My Account" noindex />
      <section className="pt-32 pb-20 bg-cream-100" data-testid="profile-page">
        <div className="container-tti">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-2">Your dashboard</div>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Namaste, {(user.full_name || user.name || "").split(" ")[0]} 🙏</h1>
            </div>
            <button onClick={logout} data-testid="profile-logout" className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-deepred">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>

          <Tabs defaultValue="bookings">
            <TabsList className="bg-white border border-cream-200">
              <TabsTrigger value="bookings" data-testid="tab-bookings"><CalIcon className="w-4 h-4 mr-2" /> Bookings <span className="ml-2 text-xs bg-saffron text-white px-1.5 rounded">{bookings.length}</span></TabsTrigger>
              <TabsTrigger value="wishlist" data-testid="tab-wishlist"><Heart className="w-4 h-4 mr-2" /> Wishlist <span className="ml-2 text-xs bg-deepred text-white px-1.5 rounded">{wishlist.length}</span></TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2" /> Profile</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6">
              {loading ? <div className="text-center py-12 text-foreground/60"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : bookings.length === 0 ? (
                <div className="bg-white border border-cream-200 rounded-sm p-10 text-center">
                  <CalIcon className="w-12 h-12 text-saffron mx-auto mb-4" />
                  <h3 className="font-serif text-2xl mb-2">No bookings yet</h3>
                  <p className="text-foreground/60 mb-6">Start exploring our curated journeys.</p>
                  <Link to="/tours" className="btn-primary">Browse tours</Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {bookings.map((b) => (
                    <div key={b.id} className="bg-white border border-cream-200 rounded-sm p-5" data-testid={`booking-${b.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-serif text-xl leading-tight">{b.tour_title}</h3>
                        <div className="flex flex-col gap-1 text-[10px] uppercase tracking-wider">
                          <span className={`px-2 py-0.5 rounded ${STATUS_BADGES[b.status]||""}`}>{b.status}</span>
                          <span className={`px-2 py-0.5 rounded ${PAY_BADGES[b.payment_status]||""}`}>{b.payment_status}</span>
                        </div>
                      </div>
                      <div className="text-sm space-y-1 text-foreground/75">
                        <div className="flex gap-2"><CalIcon className="w-3.5 h-3.5 text-saffron mt-0.5" /><span>{b.travel_date}</span></div>
                        <div>{b.adults} adults · {b.children} children</div>
                        <div className="text-foreground font-medium pt-1">${b.total_usd} USD</div>
                      </div>
                      <div className="mt-4 flex gap-2 flex-wrap">
                        <a href={`${API_BASE}/tours/${b.tour_slug}/itinerary.pdf`} download className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-cream-200 hover:bg-cream-100 rounded-sm" data-testid={`download-${b.id}`}>
                          <Download className="w-3 h-3" /> Itinerary PDF
                        </a>
                        <Link to={`/tours/${b.tour_slug}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-cream-200 hover:bg-cream-100 rounded-sm">View tour</Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="wishlist" className="mt-6">
              {loading ? <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : wishlist.length === 0 ? (
                <div className="bg-white border border-cream-200 rounded-sm p-10 text-center">
                  <Heart className="w-12 h-12 text-saffron mx-auto mb-4" />
                  <h3 className="font-serif text-2xl mb-2">Wishlist is empty</h3>
                  <p className="text-foreground/60 mb-6">Click the heart on any tour to save it here.</p>
                  <Link to="/tours" className="btn-primary">Browse tours</Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {wishlist.map((t) => (
                    <div key={t.slug} className="bg-white border border-cream-200 rounded-sm overflow-hidden group" data-testid={`wishlist-${t.slug}`}>
                      <Link to={`/tours/${t.slug}`} className="block aspect-[4/3] overflow-hidden">
                        <img src={t.image_url} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </Link>
                      <div className="p-5">
                        <h3 className="font-serif text-xl leading-tight mb-1">{t.title}</h3>
                        <div className="text-xs text-foreground/60 mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.locations?.slice(0,3).join(" · ")}</div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.25em] text-foreground/55">Price starts from</div>
                            <div className="font-serif text-xl text-deepred"><Price usd={t.price_usd} /></div>
                          </div>
                          <button onClick={() => removeFromWishlist(t.slug)} className="text-xs text-foreground/60 hover:text-destructive" data-testid={`remove-wishlist-${t.slug}`}>Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="profile" className="mt-6">
              <div className="bg-white border border-cream-200 rounded-sm p-8 max-w-xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-2xl">Profile details</h3>
                  {!editing && <button onClick={() => setEditing(true)} className="text-sm text-saffron flex items-center gap-1" data-testid="edit-profile"><Edit3 className="w-3.5 h-3.5" /> Edit</button>}
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <Input value={user.email} disabled className="bg-cream-100" data-testid="profile-email" />
                  </div>
                  <div>
                    <Label>Full name</Label>
                    <Input value={form.full_name} disabled={!editing} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={editing ? "bg-white" : "bg-cream-100"} data-testid="profile-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Phone</Label>
                      <Input value={form.phone} disabled={!editing} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={editing ? "bg-white" : "bg-cream-100"} data-testid="profile-phone" />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input value={form.country} disabled={!editing} onChange={(e) => setForm({ ...form, country: e.target.value })} className={editing ? "bg-white" : "bg-cream-100"} data-testid="profile-country" />
                    </div>
                  </div>
                  {editing && (
                    <div className="flex gap-2">
                      <button onClick={saveProfile} className="btn-primary" data-testid="save-profile"><Check className="w-4 h-4 mr-2" /> Save</button>
                      <button onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </PageLayout>
  );
}
